import { ParsedFeedItem } from './types';

const GUID_PATTERN = /Buzzsprout-(\d+)/i;
const DURATION_CLOCK_PATTERN = /^(?:(\d+):)?(\d{1,2}):(\d{2})$/;

function textOf(el: Element | null): string {
  return el?.textContent?.trim() ?? '';
}

function firstTag(parent: Element, tagName: string): Element | null {
  return parent.getElementsByTagName(tagName)[0] ?? null;
}

function parseBuzzsproutId(guidText: string): number | null {
  const match = guidText.match(GUID_PATTERN);
  if (match) return Number(match[1]);
  const trailing = guidText.match(/(\d+)\s*$/);
  return trailing ? Number(trailing[1]) : null;
}

function parseDurationSeconds(raw: string): number {
  if (!raw) return 0;
  const trimmed = raw.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const clock = trimmed.match(DURATION_CLOCK_PATTERN);
  if (!clock) return 0;
  const [, h, m, s] = clock;
  return (Number(h ?? 0) * 3600) + (Number(m) * 60) + Number(s);
}

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

function parseDescription(item: Element): string {
  const summary = textOf(firstTag(item, 'itunes:summary'));
  if (summary) return summary;
  const description = textOf(firstTag(item, 'description'));
  return description ? stripHtml(description) : '';
}

export function parseFeed(xml: string): ParsedFeedItem[] {
  const doc = new DOMParser().parseFromString(xml, 'text/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error('Feed XML failed to parse');
  }

  const items = Array.from(doc.getElementsByTagName('item'));
  const result: ParsedFeedItem[] = [];

  for (const item of items) {
    const buzzsproutId = parseBuzzsproutId(textOf(firstTag(item, 'guid')));
    const enclosure = firstTag(item, 'enclosure');
    const audioUrl = enclosure?.getAttribute('url') ?? '';

    if (buzzsproutId == null || !audioUrl) continue;

    const episodeNumberRaw = textOf(firstTag(item, 'itunes:episode'));
    const episodeNumber = episodeNumberRaw ? Number(episodeNumberRaw) : undefined;

    const pubDateText = textOf(firstTag(item, 'pubDate'));
    const publishedDate = pubDateText ? new Date(pubDateText) : new Date(NaN);

    const artworkUrl = firstTag(item, 'itunes:image')?.getAttribute('href') ?? undefined;

    result.push({
      buzzsproutId,
      episodeNumber: Number.isFinite(episodeNumber) ? episodeNumber : undefined,
      title: textOf(firstTag(item, 'title')) || textOf(firstTag(item, 'itunes:title')),
      description: parseDescription(item),
      publishedDate,
      audioUrl,
      durationSeconds: parseDurationSeconds(textOf(firstTag(item, 'itunes:duration'))),
      artworkUrl,
    });
  }

  return result;
}
